import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '@/styles/ipe/workbench/IpeOwnerFilterSelect.css';

/** 分页条数等已有独立样式，不套「筛选下拉」统一皮肤 */
const SKIP_UNIFIED_FILTER_STYLE_RE = /(?:^|\s)(?:wf-tasks-pagination__page-size-select|sourcing-hub__page-size-select|funding-pagination__page-size-select)(?:\s|$)/;

const MODAL_ANCESTOR_SELECTOR =
  '.platform-integration__modal-overlay, .platform-integration__modal, .funding-modal, .wf-tasks-modal, .pod-pl__modal-overlay';

const MENU_PORTAL_Z_INDEX = 20080;
const MENU_MAX_HEIGHT = 280;
const MENU_GAP_PX = 6;

function shouldApplyUnifiedFilterStyle(className = '', variant = 'light') {
  if (variant !== 'light') return false;
  if (SKIP_UNIFIED_FILTER_STYLE_RE.test(className)) return false;
  if (/(?:^|\s)ipe-owner-filter-select(?:\s|$)/.test(className)) return false;
  return true;
}

function shouldUseMenuPortal(menuPortal, rootEl) {
  if (menuPortal === false) return false;
  if (menuPortal === true) return true;
  return Boolean(rootEl?.closest(MODAL_ANCESTOR_SELECTOR));
}

function computePortalMenuStyle(trigger) {
  if (!trigger) return null;
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX;
  const spaceAbove = rect.top - MENU_GAP_PX;
  const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
  const maxHeight = Math.min(
    MENU_MAX_HEIGHT,
    Math.max(120, (openUp ? spaceAbove : spaceBelow) - 8),
  );

  if (openUp) {
    return {
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      top: rect.top - MENU_GAP_PX,
      transform: 'translateY(-100%)',
      maxHeight,
      zIndex: MENU_PORTAL_Z_INDEX,
    };
  }

  return {
    position: 'fixed',
    left: rect.left,
    width: rect.width,
    top: rect.bottom + MENU_GAP_PX,
    maxHeight,
    zIndex: MENU_PORTAL_Z_INDEX,
  };
}

/**
 * 工作台工具条下拉。
 * 默认 light +「全部归属人」统一筛选样式；分页条数等特殊下拉自动排除。
 * menuPortal：弹窗内默认挂到 body，避免被 modal overflow 裁切。
 */
export default function IpeToolbarSelect({
  id,
  value,
  onChange,
  options = [],
  className = '',
  variant = 'light',
  ariaLabel,
  width,
  disabled = false,
  clearable = true,
  menuPortal = 'auto',
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [portalMenuStyle, setPortalMenuStyle] = useState(null);

  const usePortalNow = open && !disabled && shouldUseMenuPortal(menuPortal, rootRef.current);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );
  const displayLabel = selected?.label ?? options[0]?.label ?? '';
  const displayColor = String(selected?.color || options[0]?.color || '').trim();
  const hasEmptyOption = options.some((o) => o.value === '' || o.value == null);
  const hasSelection = value !== '' && value != null;
  const showClear = clearable && hasEmptyOption && hasSelection && !disabled;

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange('');
    setOpen(false);
  };

  const updatePortalMenuPosition = useCallback(() => {
    const trigger = rootRef.current?.querySelector('.funding-toolbar-select__trigger');
    setPortalMenuStyle(computePortalMenuStyle(trigger));
  }, []);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open || !shouldUseMenuPortal(menuPortal, rootRef.current)) {
      setPortalMenuStyle(null);
      return undefined;
    }

    updatePortalMenuPosition();
    window.addEventListener('resize', updatePortalMenuPosition);
    window.addEventListener('scroll', updatePortalMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updatePortalMenuPosition);
      window.removeEventListener('scroll', updatePortalMenuPosition, true);
    };
  }, [open, menuPortal, updatePortalMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const style = width ? { width, minWidth: width, maxWidth: width } : undefined;
  const unifiedClass = shouldApplyUnifiedFilterStyle(className, variant)
    ? ' ipe-owner-filter-select'
    : '';

  const menuNode = open && !disabled ? (
    <ul
      ref={usePortalNow ? menuRef : undefined}
      className={`funding-toolbar-select__menu${usePortalNow ? ' funding-toolbar-select__menu--portal' : ''}`}
      role="listbox"
      aria-labelledby={id}
      style={usePortalNow ? portalMenuStyle ?? computePortalMenuStyle(rootRef.current?.querySelector('.funding-toolbar-select__trigger')) ?? undefined : undefined}
    >
      {options.map((opt) => {
        const isActive = String(opt.value) === String(value);
        const optionColor = String(opt.color || '').trim();
        return (
          <li key={opt.value === '' || opt.value == null ? '__empty__' : String(opt.value)} role="none">
            <button
              type="button"
              role="option"
              aria-selected={isActive}
              className={`funding-toolbar-select__option${isActive ? ' funding-toolbar-select__option--active' : ''}`}
              style={optionColor ? { color: optionColor } : undefined}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`funding-toolbar-select${variant === 'light' ? ' funding-toolbar-select--light' : ''}${disabled ? ' funding-toolbar-select--disabled' : ''}${showClear ? ' funding-toolbar-select--has-value' : ''}${unifiedClass}${className ? ` ${className}` : ''}`}
      data-open={open && !disabled}
      style={style}
    >
      <button
        type="button"
        id={id}
        className="funding-toolbar-select__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <span
          className="funding-toolbar-select__value"
          style={displayColor ? { color: displayColor } : undefined}
        >
          {displayLabel}
        </span>
        {!showClear && <span className="funding-toolbar-select__chevron" aria-hidden />}
      </button>
      {showClear && (
        <button
          type="button"
          className="funding-toolbar-select__clear"
          onClick={handleClear}
          aria-label={`清空${ariaLabel || '筛选'}`}
        >
          ×
        </button>
      )}
      {menuNode && (usePortalNow ? createPortal(menuNode, document.body) : menuNode)}
    </div>
  );
}
